import * as AlertDialogPrimitive from '@radix-ui/react-alert-dialog';
import { AlertTriangle, CheckCircle2, Info } from 'lucide-react';
import styles from './ConfirmDialog.module.css';

/**
 * The confirmation modal for the authenticated app.
 *
 * Replaces seven hand-assembled AlertDialog blocks that each rebuilt their own
 * header, footer and confirm-button colour. Two things were wrong with that:
 *
 *   1. Severity was invisible. Every confirm button rendered Bootstrap blue,
 *      including "Delete User". The per-site inline colours never applied —
 *      index.js imports Bootstrap globally and its
 *      `.bg-primary { background-color: rgba(13,110,253,...) !important }`
 *      outranks an inline style. shadcn's buttonVariants() emits exactly that
 *      class name, so the two collided and Bootstrap won, silently.
 *   2. Nothing kept the seven copies consistent with each other.
 *
 * The buttons below use .mcl-btn / .mcl-btn-danger from authenticated.css, which
 * carry no `bg-*` utility name for Bootstrap to hijack. Do not "simplify" them
 * back to buttonVariants() — that reintroduces the collision, and it will look
 * fine in the markup while rendering the wrong colour.
 *
 * `tone` drives the icon, the confirm button and the warning strip together, so a
 * destructive action cannot accidentally present as a friendly one.
 */

const TONES = {
  danger: { Icon: AlertTriangle, buttonClass: 'mcl-btn-danger' },
  success: { Icon: CheckCircle2, buttonClass: 'mcl-btn-success' },
  primary: { Icon: Info, buttonClass: 'mcl-btn-primary' },
};

const ConfirmDialog = ({
  open,
  onOpenChange,
  tone = 'primary',
  title,
  description,
  warning,
  children,
  confirmLabel,
  busyLabel,
  busy = false,
  confirmDisabled = false,
  onConfirm,
  cancelLabel = 'Cancel',
  testId,
}) => {
  const { Icon, buttonClass } = TONES[tone] || TONES.primary;

  return (
    <AlertDialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <AlertDialogPrimitive.Portal>
        <AlertDialogPrimitive.Overlay className={styles.overlay} />
        <AlertDialogPrimitive.Content className={styles.content} data-testid="confirm-dialog" data-tone={tone}>
          <div className={styles.header}>
            <span className={styles.icon} data-tone={tone} data-testid="confirm-dialog-icon" aria-hidden="true">
              <Icon size={18} />
            </span>
            <AlertDialogPrimitive.Title className={styles.title} data-testid="confirm-dialog-title">
              {title}
            </AlertDialogPrimitive.Title>
          </div>

          {description && (
            <AlertDialogPrimitive.Description className={styles.description} data-testid="confirm-dialog-description">
              {description}
            </AlertDialogPrimitive.Description>
          )}

          {/* Rich content lives here rather than inside Description, which Radix
              renders as a <p> — CompareOffers used to nest <div>s in it. */}
          {children && <div className={styles.body}>{children}</div>}

          {warning && (
            <div className={styles.warning} data-tone={tone} data-testid="confirm-dialog-warning">
              {warning}
            </div>
          )}

          <div className={styles.footer}>
            <AlertDialogPrimitive.Cancel
              className="mcl-btn mcl-btn-outline"
              disabled={busy}
              data-testid="confirm-dialog-cancel"
            >
              {cancelLabel}
            </AlertDialogPrimitive.Cancel>
            <AlertDialogPrimitive.Action
              className={`mcl-btn ${buttonClass}`}
              disabled={busy || confirmDisabled}
              data-testid={testId}
              // Radix closes on Action click. Every one of these handlers is async
              // and several need the dialog to stay open while they run, so the
              // call sites keep owning when it closes.
              onClick={(e) => {
                e.preventDefault();
                onConfirm?.(e);
              }}
            >
              {busy && busyLabel ? busyLabel : confirmLabel}
            </AlertDialogPrimitive.Action>
          </div>
        </AlertDialogPrimitive.Content>
      </AlertDialogPrimitive.Portal>
    </AlertDialogPrimitive.Root>
  );
};

export default ConfirmDialog;
